import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  experimentService,
  playbookBankService,
  GetWizardEstimateResult,
  PlaybookRisk,
  VariantType,
} from 'src/services';
import { useMountRef } from 'src/components-dummy/hooks/use-mount-ref';
import {
  BUILD_YOUR_OWN_RISK,
  CUSTOM_PRODUCTION_STEP,
  DEFAULT_CUSTOM_PRODUCTION,
  DEFAULT_MDE,
  ESTIMATE_DEBOUNCE_MS,
  MDE_OPTIONS,
  RISK_TO_PRESET_ID,
  TRAFFIC_PRESETS,
} from './constants';
import { TrafficPresetId } from '../wizard-risk-split';
import {
  MdeOption,
  TrafficSelection,
  TrafficSplit,
  TrafficStepProps,
  UseTrafficStepReturnType,
} from './types';

function findMatchingPreset(split: TrafficSplit): TrafficPresetId | undefined {
  return TRAFFIC_PRESETS.find(
    preset =>
      preset.split.production === split.production &&
      preset.split.control === split.control &&
      preset.split.variant === split.variant
  )?.id;
}

function splitFromCustomProduction(production: number): TrafficSplit {
  // production is always a multiple of CUSTOM_PRODUCTION_STEP (10), so the
  // remainder is always even and splits into two equal integer arms.
  const halfRemainder = (100 - production) / 2;
  return { production, control: halfRemainder, variant: halfRemainder };
}

export const useTrafficStep = ({
  shopId,
  experiment,
  goalMetric,
}: TrafficStepProps): UseTrafficStepReturnType => {
  const wizardSelection = experiment?.wizardSelection;
  const controlVariant = experiment?.variants.find(variant => variant.type === VariantType.Control);
  const testVariant = experiment?.variants.find(variant => variant.type === VariantType.Test);

  const [risk, setRisk] = useState<PlaybookRisk>(BUILD_YOUR_OWN_RISK);

  const mountRef = useMountRef();

  useEffect(() => {
    if (wizardSelection?.mode !== 'playbooks' || !wizardSelection.selectedPlaybookId) {
      setRisk(BUILD_YOUR_OWN_RISK);
      return;
    }

    playbookBankService
      .getPlaybookBank({ shopId })
      .then(bank => {
        if (mountRef.current.hasUnmounted) return;
        const playbook = bank.find(item => item.id === wizardSelection.selectedPlaybookId);
        setRisk(playbook?.risk ?? BUILD_YOUR_OWN_RISK);
      })
      .catch(() => {
        if (!mountRef.current.hasUnmounted) setRisk(BUILD_YOUR_OWN_RISK);
      });
  }, [shopId, wizardSelection?.mode, wizardSelection?.selectedPlaybookId, mountRef]);

  const recommendedPresetId = RISK_TO_PRESET_ID[risk];

  const savedProduction = experiment?.productionTrafficAllocation;
  const savedSplit: TrafficSplit | undefined =
    savedProduction !== null && savedProduction !== undefined && controlVariant && testVariant
      ? {
          production: savedProduction,
          control: controlVariant.trafficAllocation,
          variant: testVariant.trafficAllocation,
        }
      : undefined;

  const [presetId, setPresetId] = useState<TrafficSelection>(() => {
    if (savedSplit) {
      return findMatchingPreset(savedSplit) ?? 'custom';
    }
    return recommendedPresetId;
  });

  const [customProduction, setCustomProductionState] = useState<number>(
    savedSplit && findMatchingPreset(savedSplit) === undefined
      ? savedSplit.production
      : DEFAULT_CUSTOM_PRODUCTION
  );

  const split: TrafficSplit = useMemo(() => {
    if (presetId === 'custom') {
      return splitFromCustomProduction(customProduction);
    }
    const matchedPreset = TRAFFIC_PRESETS.find(preset => preset.id === presetId);
    return matchedPreset?.split ?? splitFromCustomProduction(DEFAULT_CUSTOM_PRODUCTION);
  }, [presetId, customProduction]);

  const [selectedMde, setSelectedMde] = useState<number>(
    experiment?.estimatorInputs?.mde ?? DEFAULT_MDE
  );
  const [autoStopOnSevereDrop, setAutoStopOnSevereDrop] = useState<boolean>(
    experiment?.guardrails?.autoStopOnSevereDrop ?? true
  );
  const [emailVerdict] = useState<boolean>(experiment?.guardrails?.emailVerdict ?? false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [estimates, setEstimates] = useState<Record<number, GetWizardEstimateResult | undefined>>(
    {}
  );
  const [isLoadingEstimates, setIsLoadingEstimates] = useState(true);

  useEffect(() => {
    if (!goalMetric || split.variant <= 0) {
      setEstimates({});
      setIsLoadingEstimates(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoadingEstimates(true);

    const timeoutId = setTimeout(() => {
      Promise.all(
        MDE_OPTIONS.map(mde =>
          experimentService
            .getWizardEstimate({ shopId, metric: goalMetric, mde, armShare: split.variant / 100 })
            .then(result => [mde, result] as const)
            .catch(
              () => [mde, { status: 'unavailable', reason: 'Failed to load estimate' }] as const
            )
        )
      ).then(results => {
        if (cancelled) return;
        setEstimates(Object.fromEntries(results));
        setIsLoadingEstimates(false);
      });
    }, ESTIMATE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [shopId, goalMetric, split.variant]);

  const mdeOptions: MdeOption[] = useMemo(
    () => MDE_OPTIONS.map(mde => ({ mde, estimate: estimates[mde] })),
    [estimates]
  );

  const selectPreset = useCallback((id: TrafficPresetId) => {
    setPresetId(id);
  }, []);

  const setCustomProduction = useCallback((value: number) => {
    const clamped = Math.max(
      0,
      Math.min(100, Math.round(value / CUSTOM_PRODUCTION_STEP) * CUSTOM_PRODUCTION_STEP)
    );
    setPresetId('custom');
    setCustomProductionState(clamped);
  }, []);

  const canContinue = useMemo(() => !isSubmitting, [isSubmitting]);

  const onSubmit = useCallback((): Promise<boolean> => {
    if (!experiment || !controlVariant || !testVariant) {
      return Promise.resolve(false);
    }

    setIsSubmitting(true);

    const selectedEstimate = estimates[selectedMde];

    return experimentService
      .updateExperiment({
        shopId,
        experiment: {
          id: experiment.id,
          productionTrafficAllocation: split.production,
          variants: experiment.variants.map(variant => ({
            id: variant.id,
            name: variant.name,
            type: variant.type,
            trafficAllocation:
              variant.id === controlVariant.id
                ? split.control
                : variant.id === testVariant.id
                  ? split.variant
                  : variant.trafficAllocation,
          })),
          guardrails: { autoStopOnSevereDrop, emailVerdict },
          estimatorInputs:
            goalMetric && selectedEstimate?.status === 'ok'
              ? {
                  metric: goalMetric,
                  mde: selectedMde,
                  dailyTestUsers: selectedEstimate.dailyTestUsers,
                  requiredSamplePerArm: selectedEstimate.requiredSamplePerArm,
                  estimatedDays: selectedEstimate.estimatedDays,
                  confidenceLevel: selectedEstimate.confidenceLevel,
                  frozenAt: new Date(),
                }
              : null,
        },
      })
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [
    experiment,
    controlVariant,
    testVariant,
    shopId,
    split,
    autoStopOnSevereDrop,
    emailVerdict,
    goalMetric,
    estimates,
    selectedMde,
  ]);

  return {
    split,
    presetId,
    selectPreset,
    customProduction,
    setCustomProduction,
    recommendedPresetId,
    mdeOptions,
    selectedMde,
    setSelectedMde,
    isLoadingEstimates,
    autoStopOnSevereDrop,
    setAutoStopOnSevereDrop,
    emailVerdict,
    canContinue,
    isSubmitting,
    onSubmit,
  };
};
